import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useBranding } from "@/context/BrandingContext";
import { LogoutConfirmDialog } from "@/components/LogoutConfirmDialog";
import { api, inr } from "@/lib/api";
import { getAdminUrl } from "@/lib/host";
import { FoodSymbol } from "@/components/FoodSymbol";

const getRecentSearches = () => {
  try {
    const saved = JSON.parse(localStorage.getItem("eira_recent_searches") || "[]");
    return Array.isArray(saved) ? saved.filter((item) => typeof item === "string") : [];
  } catch {
    return [];
  }
};

const couponTickerText = (coupon) => {
  if (coupon.kind === "percent") return `${coupon.value}% off`;
  if (coupon.kind === "free_shipping") return "Free shipping";
  if (coupon.kind === "bogo") return "Buy 1 Get 1 Free";
  if (coupon.kind === "free_item") return "Free item included";
  return `${inr(coupon.value)} off`;
};

const RecentSearchesPanel = ({ terms, onPick, onRemove, onClear, testIdPrefix }) => (
  <div
    className="absolute right-0 top-full z-50 mt-2 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-[#E2DCD2] bg-white shadow-xl"
    data-testid={`${testIdPrefix}-recent-searches`}
  >
    <div className="flex items-center justify-between border-b border-[#E2DCD2] px-4 py-2.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-[#8C6D46]">Recent searches</span>
      <button
        type="button"
        onMouseDown={(event) => event.preventDefault()}
        onClick={onClear}
        className="text-xs font-semibold text-[#C86D42] hover:underline"
        data-testid={`clear-${testIdPrefix}-recent-searches`}
      >
        Clear all
      </button>
    </div>
    {terms.map((term, index) => (
      <div key={term} className="flex items-center border-b border-[#F4F1EA] last:border-b-0">
        <button
          type="button"
          onMouseDown={(event) => {
            event.preventDefault();
            onPick(term);
          }}
          className="min-w-0 flex-1 truncate px-4 py-3 text-left text-sm hover:bg-[#F4F1EA]"
          data-testid={`${testIdPrefix}-recent-search-${index}`}
        >
          {term}
        </button>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onRemove(term)}
          className="mr-2 rounded p-1.5 text-[#64748B] hover:bg-red-50 hover:text-[#7F1D1D]"
          aria-label={`Remove ${term} from recent searches`}
          data-testid={`remove-${testIdPrefix}-recent-search-${index}`}
        >
          <X size={14} />
        </button>
      </div>
    ))}
  </div>
);

const Header = () => {
  const { user, wishlistIds, logout } = useAuth();
  const { count, setOpen } = useCart();
  const { logo_url, logo_alt, tagline, loading: brandingLoading } = useBranding();
  const nav = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [recentSearches, setRecentSearches] = useState(getRecentSearches);
  const [suggest, setSuggest] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [cats, setCats] = useState([]);
  const [camps, setCamps] = useState([]);
  const [brands, setBrands] = useState([]);
  const [mobile, setMobile] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [mobileActive, setMobileActive] = useState("");
  const mobileMenuRef = useRef(null);
  const userMenuRef = useRef(null);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const adminUrl = user?.role === "admin" ? getAdminUrl() : "";
  const shopParams = new URLSearchParams(location.search);
  const activeCategory = location.pathname === "/shop" ? shopParams.get("category") : "";
  const collectionTag = location.pathname === "/shop" ? shopParams.get("tag") : "";
  const desktopNavClass = (isActive) => [
    "transition-colors",
    isActive
      ? "text-[#C86D42] font-semibold"
      : "text-[#1E2A24] hover:text-[#C86D42]",
  ].join(" ");
  // Reset broken flag whenever the URL changes so a new upload can retry rendering.
  useEffect(() => { setLogoBroken(false); setLogoLoaded(false); }, [logo_url]);
  useEffect(() => { api.get("/coupons").then((response) => setCoupons(response.data)).catch(() => {}); }, []);
  useEffect(() => {
    const closeMobileMenu = (event) => {
      if (mobile && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) setMobile(false);
    };
    document.addEventListener("mousedown", closeMobileMenu);
    return () => document.removeEventListener("mousedown", closeMobileMenu);
  }, [mobile]);

  useEffect(() => {
    const closeUserMenu = (event) => {
      if (userMenu && userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenu(false);
      }
    };
    document.addEventListener("pointerdown", closeUserMenu);
    return () => document.removeEventListener("pointerdown", closeUserMenu);
  }, [userMenu]);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/campaigns").then((r) => setCamps(r.data)).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (q.trim().length > 1) {
        api.get(`/products/search?q=${encodeURIComponent(q)}`).then((r) => setSuggest(r.data));
      } else setSuggest([]);
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const saveRecentSearch = (value) => {
    const term = value.trim().replace(/\s+/g, " ");
    if (!term) return "";
    const normalizedTerm = term.toLocaleLowerCase();
    const next = [term, ...recentSearches.filter((item) => item.toLocaleLowerCase() !== normalizedTerm)].slice(0, 5);
    setRecentSearches(next);
    localStorage.setItem("eira_recent_searches", JSON.stringify(next));
    return term;
  };
  const storeRecentSearches = (next) => {
    setRecentSearches(next);
    localStorage.setItem("eira_recent_searches", JSON.stringify(next));
  };
  const removeRecentSearch = (term) => {
    storeRecentSearches(recentSearches.filter((item) => item !== term));
  };
  const clearRecentSearches = () => {
    storeRecentSearches([]);
  };
  const selectRecentSearch = (term) => {
    setQ(term);
    nav(`/shop?q=${encodeURIComponent(term)}`);
    setShowSuggest(false);
  };
  const closeUserMenu = () => setUserMenu(false);
  const onSearch = (e) => {
    e.preventDefault();
    const term = saveRecentSearch(q);
    if (term) {
      nav(`/shop?q=${encodeURIComponent(term)}`);
      setShowSuggest(false);
    }
  };
  const confirmLogout = async () => {
    setLogoutBusy(true);
    try {
      await logout();
    } finally {
      setLogoutBusy(false);
      setLogoutOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-[#FDFBF7]/90 border-b border-[#E2DCD2] shadow-sm">
      <div
        className="overflow-hidden bg-[#2D5A47] py-2 text-xs text-white"
        data-testid="header-coupon-ticker"
        role="status"
        aria-live="polite"
      >
        {coupons.length > 0 ? (
          <div className="header-coupon-track flex w-max gap-12 whitespace-nowrap">
            {[...coupons, ...coupons].map((coupon, index) => (
              <span
                key={`${coupon.id}-${index}`}
                className="tracking-widest uppercase"
                data-testid={`header-coupon-ticker-item-${coupon.id}-${index}`}
              >
                Use code <b>{coupon.code}</b> — Get {couponTickerText(coupon)}
              </span>
            ))}
          </div>
        ) : (
          <div
            className="mx-auto max-w-7xl px-4 text-center tracking-widest uppercase"
            data-testid="header-shipping-announcement"
          >
            Free shipping on orders above ₹499 · COD available
          </div>
        )}
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 md:py-4 flex items-center gap-2 md:gap-6">
        <button
          className="lg:hidden shrink-0"
          data-testid="mobile-menu-btn"
          onClick={() => setMobile(!mobile)}
        >
          {mobile ? <X /> : <Menu />}
        </button>
        <Link to="/" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "auto" })} className="flex min-w-0 items-center gap-2" data-testid="brand-logo">
          {brandingLoading ? (
            <div
              className="h-10 w-[104px] animate-pulse rounded bg-[#E2DCD2] sm:h-11 sm:w-[140px] md:h-14 md:w-[220px]"
              data-testid="brand-logo-skeleton"
            />
          ) : logo_url && !logoBroken ? (
            <div className="relative h-10 w-[104px] sm:h-11 sm:w-[140px] md:h-14 md:w-[220px]">
              {!logoLoaded && (
                <div
                  className="absolute inset-0 animate-pulse rounded bg-[#E2DCD2]"
                  data-testid="brand-logo-skeleton"
                />
              )}
              <img
                src={logo_url}
                alt={logo_alt || "Eira"}
                className={`h-full w-full object-contain transition-opacity ${logoLoaded ? "opacity-100" : "opacity-0"}`}
                onLoad={() => setLogoLoaded(true)}
                onError={() => setLogoBroken(true)}
                data-testid="brand-logo-img"
              />
            </div>
          ) : (
            <>
              <div className="w-10 h-10 rounded-full bg-[#2D5A47] flex items-center justify-center">
                <span className="font-serif text-white text-xl italic">
                  {(logo_alt || "E").trim().charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex flex-col leading-none">
                <span className="font-serif text-2xl font-semibold text-[#1E2A24]">
                  {logo_alt || "Eira"}
                </span>
                <span className="text-[10px] tracking-[0.25em] uppercase text-[#8C6D46]">{tagline || "Pure · Essential"}</span>
              </div>
            </>
          )}
        </Link>

        <form
          onSubmit={onSearch}
          className="relative flex min-w-0 flex-1 md:hidden"
          data-testid="mobile-search-form"
        >
          <label className="sr-only" htmlFor="mobile-search-input">
            Search Eira products
          </label>
          <input
            id="mobile-search-input"
            data-testid="mobile-search-input"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setShowSuggest(true);
            }}
            onFocus={() => setShowSuggest(true)}
            onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
            placeholder="Search"
            className="w-full min-w-0 bg-white border border-[#E2DCD2] rounded-full px-3 py-2 pr-10 text-base focus:ring-2 focus:ring-[#2D5A47] focus:border-transparent outline-none"
          />
          <button
            type="submit"
            className="absolute right-1 top-1 bg-[#2D5A47] text-white rounded-full p-1.5 hover:bg-[#214435]"
            data-testid="mobile-search-submit"
            aria-label="Search products"
          >
            <Search size={15} />
          </button>
          {showSuggest && suggest.length > 0 && (
            <div
              className="absolute right-0 top-full z-50 mt-2 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-[#E2DCD2] bg-white shadow-xl"
              data-testid="mobile-search-suggest"
            >
              {suggest.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    saveRecentSearch(p.name);
                    nav(`/product/${p.slug}`);
                    setShowSuggest(false);
                    setQ("");
                  }}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-[#F4F1EA]"
                  data-testid={`mobile-search-suggest-${p.id}`}
                >
                  <img src={p.images?.[0]} alt="" className="h-10 w-10 rounded object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-1">
                      <FoodSymbol
                        value={p.food_symbol}
                        className="mt-0.5"
                        testId={`mobile-search-food-symbol-${p.id}`}
                      />
                      <div className="truncate text-sm font-medium">{p.name}</div>
                    </div>
                    <div className="text-xs text-gray-500">{inr(p.price)}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {showSuggest && !q && recentSearches.length > 0 && (
            <RecentSearchesPanel
              terms={recentSearches}
              onPick={selectRecentSearch}
              onRemove={removeRecentSearch}
              onClear={clearRecentSearches}
              testIdPrefix="mobile"
            />
          )}
        </form>

        <form onSubmit={onSearch} className="hidden md:flex flex-1 relative" data-testid="search-form">
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setShowSuggest(true);
            }}
            onFocus={() => setShowSuggest(true)}
            onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
            placeholder="Search for spices, oils, cookware, jewellery…"
            className="w-full bg-white border border-[#E2DCD2] rounded-full px-5 py-2.5 pr-12 text-sm focus:ring-2 focus:ring-[#2D5A47] focus:border-transparent outline-none"
          />
          <button
            type="submit"
            className="absolute right-1.5 top-1.5 bg-[#2D5A47] text-white rounded-full p-2 hover:bg-[#214435]"
            data-testid="search-submit"
            aria-label="Search products"
          >
            <Search size={16} />
          </button>
          {showSuggest && suggest.length > 0 && (
            <div className="absolute top-full mt-2 w-full bg-white border border-[#E2DCD2] rounded-xl shadow-xl overflow-hidden z-50" data-testid="search-suggest">
              {suggest.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    saveRecentSearch(p.name);
                    nav(`/product/${p.slug}`);
                    setShowSuggest(false);
                    setQ("");
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F4F1EA] text-left"
                >
                  <img src={p.images?.[0]} alt="" className="w-10 h-10 rounded object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-1">
                      <FoodSymbol
                        value={p.food_symbol}
                        className="mt-0.5"
                        testId={`desktop-search-food-symbol-${p.id}`}
                      />
                      <div className="truncate text-sm font-medium">{p.name}</div>
                    </div>
                    <div className="text-xs text-gray-500">{inr(p.price)}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {showSuggest && !q && recentSearches.length > 0 && (
            <RecentSearchesPanel
              terms={recentSearches}
              onPick={selectRecentSearch}
              onRemove={removeRecentSearch}
              onClear={clearRecentSearches}
              testIdPrefix="desktop"
            />
          )}
        </form>

        <div className="ml-auto flex shrink-0 items-center gap-3 md:gap-4">
          <Link to="/wishlist" className="relative flex items-center gap-1 hover:text-[#C86D42]" data-testid="wishlist-link">
            <Heart size={20} />
            {wishlistIds.length > 0 && (
              <span
                className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#C86D42] text-[10px] font-bold text-white"
                data-testid="wishlist-count"
              >
                {wishlistIds.length}
              </span>
            )}
          </Link>
          <button onClick={() => setOpen(true)} className="relative flex items-center gap-1 hover:text-[#C86D42]" data-testid="cart-btn">
            <ShoppingBag size={20} />
            {count > 0 && (
              <span
                className="absolute -top-2 -right-2 bg-[#C86D42] text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold"
                data-testid="cart-count"
                aria-live="polite"
              >
                {count}
              </span>
            )}
          </button>
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenu((isOpen) => !isOpen)}
              className="flex items-center gap-1 hover:text-[#C86D42]"
              data-testid="user-menu-btn"
            >
              <User size={20} />
              <ChevronDown size={14} className="hidden md:block" />
            </button>
            {userMenu && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-[#E2DCD2] rounded-xl shadow-xl overflow-hidden" onMouseLeave={() => setUserMenu(false)} data-testid="user-profile-menu">
                {user ? (
                  <>
                    <div className="px-4 py-3 border-b border-[#E2DCD2]">
                      <div className="text-sm font-semibold truncate">{user.name}</div>
                      <div className="text-xs text-gray-500 truncate">{user.email}</div>
                    </div>
                    <Link
                      to="/account"
                      onClick={closeUserMenu}
                      className="block px-4 py-2 text-sm hover:bg-[#F4F1EA]"
                      data-testid="account-link"
                    >
                      My Account
                    </Link>
                    <Link
                      to="/account?tab=orders"
                      onClick={closeUserMenu}
                      className="block px-4 py-2 text-sm hover:bg-[#F4F1EA]"
                      data-testid="account-orders-link"
                    >
                      My Orders
                    </Link>
                    {adminUrl && (
                      <a
                        href={adminUrl}
                        onClick={closeUserMenu}
                        className="block px-4 py-2 text-sm hover:bg-[#F4F1EA]"
                        data-testid="admin-account-link"
                      >
                        Admin Panel
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setUserMenu(false);
                        setLogoutOpen(true);
                      }}
                      className="w-full text-left block px-4 py-2 text-sm hover:bg-[#F4F1EA]"
                      data-testid="logout-btn"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={closeUserMenu}
                      className="block px-4 py-2 text-sm hover:bg-[#F4F1EA]"
                      data-testid="login-link"
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      onClick={closeUserMenu}
                      className="block px-4 py-2 text-sm hover:bg-[#F4F1EA]"
                      data-testid="register-link"
                    >
                      Register
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <nav className="hidden lg:block border-t border-[#E2DCD2] bg-[#FDFBF7]/70">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center gap-x-6 gap-y-3 py-3 text-sm font-medium">
          <Link
            to="/"
            className={desktopNavClass(location.pathname === "/")}
            data-testid="nav-home"
          >
            Home
          </Link>
          <Link
            to="/shop"
            className={desktopNavClass(
              location.pathname === "/shop" && !activeCategory && !collectionTag,
            )}
            data-testid="nav-shop"
          >
            Shop
          </Link>
          {brands.length > 0 && (
            <div className="relative group">
              <button className="hover:text-[#C86D42] flex items-center gap-1" data-testid="nav-brands">
                Brands <ChevronDown size={12} />
              </button>
              <div className="absolute top-full left-0 pt-2 hidden group-hover:block z-40">
                <div className="bg-white border border-[#E2DCD2] rounded-xl shadow-xl overflow-hidden min-w-[220px]">
                  {brands.map((b) => (
                    <Link key={b.id} to={`/brand/${b.slug}`} className="block px-4 py-2.5 text-sm hover:bg-[#F4F1EA]" data-testid={`nav-brand-${b.slug}`}>
                      <div className="font-semibold text-[#1E2A24]">{b.name}</div>
                      <div className="text-[10px] text-[#8C6D46]">{b.tagline}</div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
          {cats.map((c) => (
            <Link
              key={c.id}
              to={`/shop?category=${c.slug}`}
              className={desktopNavClass(activeCategory === c.slug)}
              data-testid={`nav-cat-${c.slug}`}
            >
              {c.name}
            </Link>
          ))}
          <Link to="/corporate-gifts" className="font-semibold text-[#2D5A47] hover:text-[#C86D42]" data-testid="nav-corporate-gifts">Corporate Gifts</Link>
          <Link
            to="/shop?tag=new"
            className={desktopNavClass(collectionTag === "new")}
            data-testid="nav-new-arrivals"
          >
            New Arrivals
          </Link>
          <Link
            to="/shop?tag=best"
            className={desktopNavClass(collectionTag === "best")}
            data-testid="nav-best-sellers"
          >
            Best Sellers
          </Link>
          {adminUrl && (
            <a href={adminUrl} className="hover:text-[#C86D42]" data-testid="nav-admin">
              Admin
            </a>
          )}
          {camps.slice(0, 2).map((c) => (
            <Link key={c.id} to={`/campaign/${c.slug}`} className="hover:text-[#C86D42] font-semibold flex items-center gap-1" data-testid={`nav-camp-${c.slug}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#D99B26] animate-pulse" /> {c.title.length > 22 ? c.title.slice(0, 22) + "…" : c.title}
            </Link>
          ))}
        </div>
      </nav>

      {mobile && (
        <div ref={mobileMenuRef} className="lg:hidden border-t border-[#E2DCD2] bg-white px-4 py-4 space-y-2">
          <Link to="/" onClick={() => { setMobileActive("home"); setMobile(false); }} className={`block py-2 ${mobileActive === "home" || location.pathname === "/" ? "text-[#C86D42] font-semibold" : ""}`}>Home</Link>
          <Link to="/shop" onClick={() => { setMobileActive("shop"); setMobile(false); }} className={`block py-2 ${mobileActive === "shop" || location.pathname === "/shop" && !location.search ? "text-[#C86D42] font-semibold" : ""}`}>Shop</Link>
          <Link to="/corporate-gifts" onClick={() => setMobile(false)} className="block py-2 font-semibold text-[#2D5A47]" data-testid="mobile-nav-corporate-gifts">Corporate Gifts</Link>
          <Link
            to="/shop?tag=new"
            onClick={() => { setMobileActive("new"); setMobile(false); }}
            className={`block py-2 ${
              mobileActive === "new" || location.search.includes("tag=new")
                ? "text-[#C86D42] font-semibold"
                : ""
            }`}
            data-testid="mobile-nav-new-arrivals"
          >
            New Arrivals
          </Link>
          <Link
            to="/shop?tag=best"
            onClick={() => { setMobileActive("best"); setMobile(false); }}
            className={`block py-2 ${
              mobileActive === "best" || location.search.includes("tag=best")
                ? "text-[#C86D42] font-semibold"
                : ""
            }`}
            data-testid="mobile-nav-best-sellers"
          >
            Best Sellers
          </Link>
          {camps.slice(0, 2).map((campaign) => (
            <Link
              key={campaign.id}
              to={`/campaign/${campaign.slug}`}
              onClick={() => { setMobileActive(`campaign-${campaign.slug}`); setMobile(false); }}
              className={`block py-2 ${
                mobileActive === `campaign-${campaign.slug}`
                  || location.pathname === `/campaign/${campaign.slug}`
                  ? "text-[#C86D42] font-semibold"
                  : ""
              }`}
              data-testid={`mobile-nav-campaign-${campaign.slug}`}
            >
              {campaign.title}
            </Link>
          ))}
          {adminUrl && (
            <a
              href={adminUrl}
              className="block py-2 text-[#C86D42] font-semibold"
              data-testid="mobile-nav-admin"
            >
              Admin Panel
            </a>
          )}
          {cats.map((c) => (
            <Link key={c.id} to={`/shop?category=${c.slug}`} onClick={() => { setMobileActive(`category-${c.slug}`); setMobile(false); }} className={`block py-2 ${mobileActive === `category-${c.slug}` || location.search.includes(`category=${c.slug}`) ? "text-[#C86D42] font-semibold" : ""}`}>
              {c.name}
            </Link>
          ))}
        </div>
      )}
      <LogoutConfirmDialog
        open={logoutOpen}
        busy={logoutBusy}
        onCancel={() => setLogoutOpen(false)}
        onConfirm={confirmLogout}
        testIdPrefix="header"
      />
    </header>
  );
};

export default Header;
